import { useEffect, useState } from 'react'
import { Phone, Plus, RefreshCw, Search, UserPlus } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { ActiveFilters, AsyncMultiSelect, FilterBar, SearchInput } from '@/shared/ui/filters'
import { CursorPager } from '@/shared/ui/CursorPager'
import { ErrorStrip, Forbidden, LoadingRows } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { useCursorPage } from '@/shared/hooks/useCursorPage'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { listUnits } from '@/features/geography/api'
import { fmtNumber, fmtRelative } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { MobilizerResponse } from '@/shared/api/types'
import { listMobilizers, lookupMobilizerByPhone, mobilizerStatistics } from './api'
import {
  ROLES, ROLE_ORDER, RoleBadge, STATUS_ORDER, StatusChip, statusLabel,
} from './vocab'
import { MobilizerDrawer } from './MobilizerDrawer'
import { MobilizerFormDialog } from './MobilizerFormDialog'
import type { Country } from 'react-phone-number-input'
import { PhoneInput, countryOf, DEFAULT_COUNTRY } from '@/components/erp/phone-input'

/**
 * The roster.
 *
 * ## Why the phone search is a dialog and not the search box
 *
 * `?q=0712345678` would put a mobilizer's phone number in the URL, and from
 * there into the access log, the browser history and the `Referer` of every
 * asset the page loads. A mobilizer contact list is a target list — for
 * poaching, for intimidation, for resale — so the server refuses a digit string
 * in `q` and names the lookup endpoint instead. This screen never gets the
 * chance to send one: the number is held in React state and posted in a body.
 * The URL only ever records that a lookup happened.
 *
 * The same reasoning is why the table shows `+254 7•• ••• 891` and the full
 * number lives behind one audited click in the drawer.
 *
 * ## The columns answer four different questions
 *
 * Where somebody works (**Home unit**, **Units**), who they answer to
 * (**Supervisor**, **Team**), what they own (**Stations**), and whether they can
 * log in at all (**Account**). That last one is not decoration: most mobilizers
 * have no login, `MOBILIZER` is a role a *user* holds, and the two are linked by
 * a nullable id rather than being the same record.
 */

const PAGE = 50

export function MobilizerRosterPage() {
  const canRead = useCan('mobilizer:read')
  const canCreate = useCan('mobilizer:create')
  const [params, setParams] = useSearchParams()

  const role = params.get('role') ?? ''
  const status = params.get('status') ?? ''
  const supervisorId = params.get('supervisorId') ?? ''
  const account = params.get('hasUserAccount') ?? ''
  const [units, setUnits] = useState<{ value: string; label: string }[]>([])

  /**
   * The search box is NOT bound straight to the URL, and that is the whole point.
   *
   * `?q=0712345678` would put a mobilizer's number in the address bar, in the
   * `Referer` of every asset the page loads after it, in the browser history and
   * in the server's access log — and it would be there *before* the API's
   * `mobilizer/phone-in-query` refusal came back. So a value that looks like a
   * phone number stops here, in component state, and the user is pointed at the
   * lookup dialog instead. Everything else is debounced into the URL as usual.
   */
  const [draft, setDraft] = useState(params.get('q') ?? '')
  const debounced = useDebounced(draft, 300)
  const looksLikePhone = /^\+?\d{9,13}$/.test(draft.replace(/[\s-]/g, ''))
  const q = looksLikePhone ? '' : debounced

  useEffect(() => {
    if (looksLikePhone) return
    const sp = new URLSearchParams(params)
    if (debounced) sp.set('q', debounced); else sp.delete('q')
    if (sp.toString() !== params.toString()) setParams(sp, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, looksLikePhone])

  const [openId, setOpenId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [lookupOpen, setLookupOpen] = useState(false)
  const [lookupPhone, setLookupPhone] = useState('')

  const patch = (next: Record<string, string | null>) => {
    const sp = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') sp.delete(k); else sp.set(k, v)
    }
    setParams(sp, { replace: true })
  }

  const unitIds = units.map((u) => u.value)
  const filters = {
    q: q || undefined,
    role: role || undefined,
    status: status || undefined,
    supervisorId: supervisorId || undefined,
    hasUserAccount: account || undefined,
    unitId: unitIds.length ? unitIds : undefined,
  }

  const page = useCursorPage<MobilizerResponse>(
    (cursor) => (canRead
      ? listMobilizers(filters, cursor, PAGE)
      : Promise.resolve({ content: [], hasMore: false })),
    [canRead, q, role, status, supervisorId, account, unitIds.join(',')])

  const stats = useLoad(
    () => (canRead ? mobilizerStatistics(filters) : Promise.resolve(null)),
    [canRead, q, role, status, supervisorId, account, unitIds.join(',')])

  if (!canRead) return <Forbidden what="the mobilizer roster" />

  const s = stats.data
  const activeCount = [q, role, status, account, supervisorId].filter(Boolean).length
    + (unitIds.length ? 1 : 0)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Mobilizer roster"
        description="The people doing field work, and where each of them works. Phone numbers are masked — revealing one is a separate, audited action."
        right={
          <div className="flex flex-wrap gap-1.5 justify-end">
            <button type="button" className="erp-btn" onClick={() => setLookupOpen(true)}>
              <Phone className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Find by phone</span>
            </button>
            <button type="button" className="erp-btn" aria-label="Refresh"
                    onClick={() => { void page.reload(); void stats.reload() }}>
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
            {canCreate && (
              <button type="button" className="erp-btn-primary" onClick={() => setCreating(true)}>
                <UserPlus className="h-3.5 w-3.5" />
                <span className="axis-hide-sm">Add mobilizer</span>
              </button>
            )}
          </div>
        }
      />

      <KpiStrip>
        <MetricCard label="Mobilizers" value={fmtNumber(s?.total ?? 0)}
                    sub="matching these filters" accent />
        <MetricCard label="Active" value={fmtNumber(s?.active ?? 0)}
                    sub={s ? `${s.total - s.active} inactive, suspended or ended` : ' '} />
        <MetricCard label="With a login" value={fmtNumber(s?.withUserAccount ?? 0)}
                    sub="can open the field screen" />
        <MetricCard label="Roles in use" value={fmtNumber(Object.keys(s?.byRole ?? {}).length)}
                    sub="of ten defined" />
        <MetricCard label="Counties covered" value={fmtNumber((s?.byUnit ?? []).length)}
                    sub="with at least one mobilizer" />
      </KpiStrip>

      <FilterBar
        activeCount={activeCount}
        right={<ActiveFilters count={activeCount} onClear={() => {
          setUnits([])
          setDraft('')
          setParams({}, { replace: true })
        }} />}
      >
        <div className="flex flex-col gap-1 min-w-0">
          <SearchInput
            value={draft}
            onChange={setDraft}
            placeholder="Search by name"
          />
          {looksLikePhone && (
            <button
              type="button"
              onClick={() => { setLookupPhone(draft); setDraft(''); setLookupOpen(true) }}
              className="text-erp-xs text-chocolate text-left hover:underline"
            >
              That looks like a phone number — search for it properly instead.
            </button>
          )}
        </div>
        <select className="erp-input" value={role} aria-label="Role"
                onChange={(e) => patch({ role: e.target.value })}>
          <option value="">All roles</option>
          {ROLE_ORDER.map((c) => <option key={c} value={c}>{ROLES[c].label}</option>)}
        </select>
        <select className="erp-input" value={status} aria-label="Status"
                onChange={(e) => patch({ status: e.target.value })}>
          <option value="">All statuses</option>
          {STATUS_ORDER.map((c) => <option key={c} value={c}>{statusLabel(c)}</option>)}
        </select>
        <select className="erp-input" value={account} aria-label="Account"
                onChange={(e) => patch({ hasUserAccount: e.target.value })}>
          <option value="">Login or not</option>
          <option value="true">Has a login</option>
          <option value="false">No login</option>
        </select>
        <AsyncMultiSelect
          label="Unit"
          selected={units}
          onChange={setUnits}
          placeholder="Search counties, wards, stations"
          search={async (term) => {
            const found = await listUnits({ q: term } as never, 0, 10)
            return found.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
          }}
        />
      </FilterBar>

      <ErrorStrip message={page.error} />

      <Panel bodyClassName="p-0">
        <ErpTable head={<>
          <Th>Mobilizer</Th>
          <Th>Role</Th>
          <Th>Home unit</Th>
          <Th className="text-right">Units</Th>
          <Th>Supervisor</Th>
          <Th className="text-right">Team</Th>
          <Th className="text-right">Stations</Th>
          <Th>Account</Th>
          <Th>Last active</Th>
          <Th>Status</Th>
        </>}>
          {page.loading ? (
            <LoadingRows colSpan={10} label="Loading the roster…" />
          ) : page.rows.length === 0 ? (
            <EmptyRow colSpan={10} message="Nobody matches these filters." />
          ) : page.rows.map((m) => (
            <tr
              key={m.id}
              onClick={() => setOpenId(m.id)}
              className="cursor-pointer hover:bg-muted/40"
              data-mobilizer={m.fullName}
            >
              <Td>
                <div className="font-medium truncate">{m.fullName}</div>
                <div className="text-erp-xs text-muted-foreground font-mono">
                  {m.maskedPhone ?? '—'}
                </div>
              </Td>
              <Td><RoleBadge code={m.roleCode} /></Td>
              <Td>
                {m.primaryUnitName
                  ? <span className="truncate">{m.primaryUnitName}</span>
                  : <span className="text-muted-foreground">No home unit</span>}
              </Td>
              <Td className="text-right erp-metric">{m.assignmentCount}</Td>
              <Td>
                {m.supervisorName ?? <span className="text-muted-foreground">No supervisor</span>}
              </Td>
              <Td className="text-right erp-metric">{m.subordinateCount || '—'}</Td>
              <Td className="text-right erp-metric">{m.adoptionCount || '—'}</Td>
              <Td>
                {m.userId
                  ? <span className="text-erp-xs text-emerald-700 dark:text-emerald-400">Linked</span>
                  : <span className="text-erp-xs text-muted-foreground">No login</span>}
              </Td>
              <Td>
                {m.lastActivityAt
                  ? <span title={m.lastActivityAt}>{fmtRelative(m.lastActivityAt)}</span>
                  : <span className="text-muted-foreground">Never</span>}
              </Td>
              <Td><StatusChip code={m.statusCode} /></Td>
            </tr>
          ))}
        </ErpTable>
        <CursorPager
          page={page.page}
          count={page.rows.length}
          hasMore={page.hasMore}
          canGoBack={page.canGoBack}
          onBack={page.back}
          onNext={page.next}
        />
      </Panel>

      {/* Keyed on the subject: `useLoad` keeps the previous `data` across a dep
          change, so without this the drawer would render person A's name, notes
          and status for one round-trip after person B was opened — with live
          buttons that would mutate A. */}
      <MobilizerDrawer
        key={openId ?? 'closed'}
        id={openId}
        onClose={() => setOpenId(null)}
        onChanged={() => { void page.reload(); void stats.reload() }}
        onOpenOther={(id) => setOpenId(id)}
      />

      <MobilizerFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(id) => { setCreating(false); void page.reload(); void stats.reload(); setOpenId(id) }}
      />

      <PhoneLookupDialog
        open={lookupOpen}
        initial={lookupPhone}
        onClose={() => { setLookupOpen(false); setLookupPhone('') }}
        onPick={(id) => { setLookupOpen(false); setLookupPhone(''); setOpenId(id) }}
      />
    </div>
  )
}

/**
 * The number is typed here, held in state, and posted in a body. It is never
 * written to the URL, so it never reaches an access log — which is the entire
 * reason this is a dialog rather than another filter in the bar above.
 */
function PhoneLookupDialog({
  open, initial, onClose, onPick,
}: { open: boolean; initial?: string; onClose: () => void; onPick: (id: string) => void }) {
  const [phone, setPhone] = useState('')
  const [phoneCountry, setPhoneCountry] = useState<Country>(DEFAULT_COUNTRY)

  // Carries over whatever the user had already typed into the search box, so
  // being redirected here costs them nothing.
  useEffect(() => { if (open) setPhone(initial ?? '') }, [open, initial])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hits, setHits] = useState<MobilizerResponse[] | null>(null)

  const run = async () => {
    if (!phone.trim()) return
    setBusy(true)
    setError(null)
    try {
      const page = await lookupMobilizerByPhone(phone, countryOf(phone, phoneCountry))
      setHits(page.content)
    } catch (err) {
      setError(explain(err))
      setHits(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { setPhone(''); setHits(null); setError(null); onClose() }}
      title="Find a mobilizer by phone"
      description="The number is sent in the request body, never in the address bar — a phone in a URL ends up in server logs and browser history."
      width="sm"
      footer={(
        <>
          <button type="button" className="erp-btn"
                  onClick={() => { setPhone(''); setHits(null); setError(null); onClose() }}>
            Close
          </button>
          <button type="button" className="erp-btn-primary" disabled={busy || !phone.trim()}
                  onClick={() => void run()}>
            <Search className="h-3.5 w-3.5" /> {busy ? 'Looking…' : 'Look up'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <PhoneInput
          value={phone}
          onChange={setPhone}
          country={phoneCountry}
          onCountryChange={setPhoneCountry}
        />
        <ErrorStrip message={error} />
        {hits !== null && (
          hits.length === 0 ? (
            <p className="text-erp-sm text-muted-foreground">
              No mobilizer holds that number. That is an answer, not an error — the
              lookup returns an empty result rather than confirming or denying that
              any particular person exists.
            </p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-md">
              {hits.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => onPick(m.id)}
                    className="w-full text-left px-3 py-2 hover:bg-muted/40 flex items-center gap-3"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-erp-sm font-medium truncate">{m.fullName}</span>
                      <span className="block text-erp-xs text-muted-foreground font-mono">
                        {m.maskedPhone}
                      </span>
                    </span>
                    <RoleBadge code={m.roleCode} />
                  </button>
                </li>
              ))}
            </ul>
          )
        )}
      </div>
    </Modal>
  )
}

/** Kept next to the roster so the "add" affordance has one obvious home. */
export const AddMobilizerIcon = Plus
